import React from 'react';
import PropTypes from 'prop-types';
import Button from 'relements/build/esm/ui/button';
import Dropdown from 'relements/build/esm/input/dropdown';
import Text from 'relements/build/esm/input/text';
import Loader from 'relements/build/esm/decorators/loader';

import hashHistory from '@src/History';

// Components
import MLabel from 'components/common/MLabel';
import Icon from 'components/Icon';

// Actions
import { createAlert, closeComponentModal } from 'actions/BotActions';
import {
  createTemplate,
  resetCreateTemplateModalStatus,
} from 'actions/TemplateActions';

// Constants
import {
  TEMPLATE_TYPES,
  TEMPLATE_CATEGORIES,
  AlertTypes,
} from '@src/constants/MogamboConstants';
import {
  CREATE_TEMPLATE_NAME_TOOLTIP,
  CREATE_TEMPLATE_DESCRIPTION_TOOLTIP,
} from '@src/constants/Tooltips';

// Stores
import BusinessStore from '@src/stores/BusinessStore';
import ActionStore from '@src/ActionStore';

// Utils
import connection from '@src/connection';
import * as Utils from '../_utils';

// Styles
import './create-template.scss';

function getStateFromStores() {
  return {
    modalStatus: ActionStore.getCreateTemplateModalStatus(),
  };
}

@Loader({})
@connection([BusinessStore, ActionStore], getStateFromStores)
export default class CreateTemplateModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      templateName: '',
      templatePurpose: '',
      taskMessage: '',
      templateCategory: 'Generic',
      templateNameValid: true,
      templatePurposeValid: true,
      taskMessageValid: true,
      templateType: TEMPLATE_TYPES.SUBSTORY_TEMPLATE,
      templateTypeOptions: [
        {
          title: 'General Steps (Bot) Smart Skill',
          value: TEMPLATE_TYPES.BOT_GENERAL_TEMPLATE,
        },
        {
          title: 'Skill Smart Skill',
          value: TEMPLATE_TYPES.SUBSTORY_TEMPLATE,
        },
      ],
    };
  }

  handleTextInput = (value, type) => {
    this.setState({
      [type]: value,
    });
  };

  handleTextInputFocus = (type) => {
    const validKey = `${type}Valid`;
    this.setState({
      [validKey]: true,
    });
  };

  updateTemplateCategory = (value) => {
    this.setState({
      templateCategory: value,
    });
  };

  updateTemplateType = (value) => {
    this.setState({
      templateType: value,
    });
  };

  onCreateTemplateClicked = () => {
    const {
      templateName,
      templatePurpose = '',
      templateCategory = 'Generic',
      taskMessage = '',
      templateType = TEMPLATE_TYPES.SUBSTORY_TEMPLATE,
    } = this.state;

    const errorData = Utils.validateTemplateFields('create', this.state);

    if (errorData.hasError) {
      this.setState({
        ...errorData.validFields,
      });

      createAlert(errorData.errorMessage, undefined, AlertTypes.NEGATIVE);
    } else {
      createTemplate(
        {
          templateName,
          templatePurpose,
          templateCategory,
          templateType,
        },
        (error) => {
          this.props.deactivateLoader();
          if (error) {
            createAlert(`${error}`, undefined, AlertTypes.NEGATIVE);
          }
        }
      );
    }
  };

  componentWillUnmount() {
    resetCreateTemplateModalStatus();
  }

  onJumpToTemplateClicked = () => {
    if (this.props.modalStatus.botId) {
      hashHistory.push(`/bot/${this.props.modalStatus.botId}`);
    }
    closeComponentModal();
  };

  renderTemplateCreationSuccess() {
    this.props.deactivateLoader();
    return (
      <div className="create-template-success-body">
        <div>
          <Icon iconType="tick-green" size={60} />
        </div>
        <div>
          {`'${this.state.templateName}' has been created successfully.`}
        </div>
        <Button
          type={Button.TYPES.PRIMARY}
          onClick={this.onJumpToTemplateClicked}
        >
          View Smart Skill
        </Button>
      </div>
    );
  }

  renderTemplateCreationForm() {
    const {
      templateName = '',
      templatePurpose = '',
      templateCategory = '',
      taskMessage = '',
      templateType = TEMPLATE_TYPES.SUBSTORY_TEMPLATE,
      templateNameValid,
      templatePurposeValid,
      taskMessageValid,
      templateTypeOptions,
    } = this.state;

    return (
      <div className="create-template-modal-body">
        <div>
          <Dropdown
            withSearch
            label="Smart Skill Type"
            className="create-template-modal-input"
            value={{
              value: templateType,
              title: templateTypeOptions[templateType].title,
            }}
            onChange={(val) => this.updateTemplateType(val.value)}
            optionKey="title"
            options={templateTypeOptions}
          />
          <Text
            label="Smart Skill Name *"
            className="create-template-modal-input"
            error={
              !templateNameValid
                ? 'Smart Skill Name can only contain A-Z, a-z, 0-9, spaces and underscore.'
                : null
            }
            value={templateName}
            onChange={(value) => this.handleTextInput(value, 'templateName')}
            onFocus={() => this.handleTextInputFocus('templateName')}
            placeholder="Enter the name of your Smart Skill"
            tooltip={CREATE_TEMPLATE_NAME_TOOLTIP.text}
          />
          <Text
            label="Smart Skill's Purpose *"
            className="create-template-modal-input"
            multiline
            error={
              !templatePurposeValid
                ? "Smart Skill's Purpose cannot be empty."
                : null
            }
            value={templatePurpose}
            onChange={(value) => this.handleTextInput(value, 'templatePurpose')}
            onFocus={() => this.handleTextInputFocus('templatePurpose')}
            placeholder="Describe the purpose of this Smart Skill"
            tooltip={CREATE_TEMPLATE_DESCRIPTION_TOOLTIP.text}
          />
          <Dropdown
            withSearch
            label="Category"
            className="create-template-modal-input"
            value={{
              title: templateCategory,
              value: templateCategory,
            }}
            onChange={(val) => this.updateTemplateCategory(val.value)}
            optionKey="title"
            options={TEMPLATE_CATEGORIES}
          />
        </div>
        {/* Todo: Add MChips here to allow adding tags for the template */}
        <div
          className="center-align-flex"
          style={{ justifyContent: 'space-between' }}
        >
          <Button
            type={Button.TYPES.PRIMARY}
            onClick={this.onCreateTemplateClicked}
          >
            Create Smart Skill
          </Button>
          <MLabel text="*Mandatory Fields" />
        </div>
      </div>
    );
  }

  render() {
    const { modalStatus = {} } = this.props;

    if (modalStatus.created) {
      return this.renderTemplateCreationSuccess();
    }
    return this.renderTemplateCreationForm();
  }
}

CreateTemplateModal.propTypes = {
  modalStatus: PropTypes.object,
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
};
